import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Plus, Info, Check } from 'lucide-react';
import { api, type MediaItem } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface MediaRowProps {
  title: string;
  items: MediaItem[];
  numbered?: boolean;
  onSelect: (id: string) => void;
  onPlay?: (id: string) => void;
  isContinueWatching?: boolean;
}

export function MediaRow({
  title,
  items,
  numbered,
  onSelect,
  onPlay,
  isContinueWatching,
}: MediaRowProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    // Fallback visible after 300ms in case intersection doesn't fire
    const fallback = setTimeout(() => setIsVisible(true), 300);
    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  if (!items || items.length === 0) return null;

  const updateScrollButtons = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 20);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 20);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.8;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
    setTimeout(updateScrollButtons, 350);
  };

  return (
    <section ref={sectionRef} className={`relative px-4 md:px-12 my-6 row-scroll-reveal ${isVisible ? 'is-visible' : ''}`}>
      {title ? (
        <h2 className="text-lg md:text-xl font-extrabold text-white mb-1 md:mb-2 tracking-tight drop-shadow row-title">
          {title}
        </h2>
      ) : null}

      <div className="group relative">
        {/* Scroll Left Button */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-0 bottom-0 z-20 w-12 bg-gradient-to-r from-black/90 via-black/50 to-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-8 w-8 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]" />
          </button>
        )}

        {/* Scroll Right Button */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-0 bottom-0 z-20 w-12 bg-gradient-to-l from-black/90 via-black/50 to-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-8 w-8 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]" />
          </button>
        )}

        {/* Scrollable Container */}
        <div
          ref={scrollRef}
          onScroll={updateScrollButtons}
          className="flex gap-2.5 md:gap-4 overflow-x-auto scrollbar-hide scroll-smooth pt-6 pb-8 px-3 md:px-5 -mx-3 md:-mx-5 -mt-3 -mb-5"
        >
          {items.map((item, index) => (
            <MediaCard
              key={item.id}
              item={item}
              index={numbered ? index + 1 : undefined}
              onSelect={onSelect}
              onPlay={onPlay}
              isContinueWatching={isContinueWatching}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function MediaCard({
  item,
  index,
  onSelect,
  onPlay,
  isContinueWatching,
}: {
  item: any;
  index?: number;
  onSelect: (id: string) => void;
  onPlay?: (id: string) => void;
  isContinueWatching?: boolean;
}) {
  const { language, t } = useThemeLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(Boolean(item.inWatchlist));

  const handleToggleWatchlist = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !inWatchlist;
    setInWatchlist(next);
    try {
      await api.toggleWatchlist(item.mediaItemId || item.id);
    } catch {
      setInWatchlist(!next);
    }
  };

  const isHebrew = language === 'he';
  const hasHebrew = Boolean(item.titleHe);
  const englishTitle = item.title || item.originalTitle || '';
  const hasBothTitles = Boolean(hasHebrew && englishTitle && item.titleHe !== englishTitle);

  const displayTitle = isHebrew
    ? (item.titleHe || englishTitle)
    : (englishTitle || item.titleHe);

  const displaySubtitle = hasBothTitles
    ? (isHebrew ? englishTitle : item.titleHe)
    : null;

  const titleDir = isHebrew ? (item.titleHe ? 'rtl' : 'ltr') : 'ltr';
  const subtitleDir = isHebrew ? 'ltr' : 'rtl';

  const imageSrc =
    (isContinueWatching ? item.backdropPath : null) ||
    item.posterPath ||
    item.backdropPath ||
    'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=500&auto=format&fit=crop&q=60';

  const remainingMinutes =
    item.duration && item.currentTime
      ? Math.max(0, Math.floor((item.duration - item.currentTime) / 60))
      : null;

  return (
    <div
      className={`relative shrink-0 ${
        isContinueWatching
          ? 'w-[210px] md:w-[290px]'
          : index !== undefined
          ? 'w-[160px] md:w-[220px] pl-6 md:pl-9'
          : 'w-[130px] md:w-[185px]'
      } group/card transition-all duration-200 ${isHovered ? 'z-30' : 'z-10'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Number badge for Top 10 (Netflix style 3D metallic typography) */}
      {index !== undefined && (
        <div className="absolute -left-3 md:-left-5 bottom-0 z-0 pointer-events-none">
          <span className="top10-number">{index}</span>
        </div>
      )}

      {/* Poster / Thumbnail Card */}
      <div
        onClick={() => onSelect(item.mediaItemId || item.id)}
        className={`relative ${
          isHovered ? 'z-30' : 'z-10'
        } ${
          isContinueWatching ? 'aspect-video' : 'aspect-[2/3]'
        } rounded-xl overflow-hidden bg-neutral-900 cursor-pointer border border-white/5 transition-all duration-300 shadow-lg card-sheen media-card-item media-card-cinematic`}
      >
        <img
          src={imageSrc}
          alt={displayTitle}
          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover/card:scale-105"
          loading="lazy"
        />

        {/* Continue Watching Minute Remaining Badge */}
        {isContinueWatching && (
          <div className="absolute top-2 left-2 z-10 pointer-events-none">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-black/85 text-emerald-400 border border-emerald-500/40 backdrop-blur-md shadow flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {remainingMinutes
                ? isHebrew
                  ? `נותרו עוד ${remainingMinutes} דק׳`
                  : `${remainingMinutes}m left`
                : item.currentTime !== undefined
                ? `${t('stoppedAtMinute')} ${Math.floor(item.currentTime / 60)} ${t('minutesShort')}`
                : ''}
            </span>
          </div>
        )}

        {/* Continue Watching Glowing Progress Bar */}
        {isContinueWatching && item.percentage !== undefined && (
          <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/20 overflow-hidden">
            <div
              className="h-full bg-[#E50914] shadow-[0_0_8px_rgba(229,9,20,0.9)]"
              style={{ width: `${Math.min(100, Math.max(0, item.percentage))}%` }}
            />
          </div>
        )}

        {/* Always-visible Title Bar on the Card (Bottom Overlay) */}
        <div
          className={`absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/95 via-black/75 to-transparent pt-7 ${
            isContinueWatching ? 'pb-3' : 'pb-2'
          } px-2.5 pointer-events-none transition-opacity duration-200 media-card-overlay ${
            isHovered ? 'md:opacity-0' : 'opacity-100'
          }`}
        >
          <p
            className="text-[11px] md:text-xs font-bold text-white leading-tight drop-shadow truncate w-full card-overlay-title"
            dir={titleDir}
            title={displayTitle}
          >
            {displayTitle}
          </p>
          {(item.year || item.rating) && (
            <div className="flex items-center justify-between mt-0.5 text-[9px] text-neutral-300 font-medium card-overlay-meta">
              {item.year && <span>{item.year}</span>}
              {item.rating && (
                <span className="text-emerald-400 font-bold ml-auto">
                  {(item.rating * 10).toFixed(0)}%
                </span>
              )}
            </div>
          )}
        </div>

        {/* Hover Overlay with details & play (desktop) */}
        <div
          className={`hidden md:flex absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent flex-col justify-end p-3 transition-opacity duration-300 z-20 media-card-hover ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {item.genres && item.genres.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap mb-1 pointer-events-none">
              {item.genres.slice(0, 2).map((g: string) => (
                <span key={g} className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-neutral-300 font-medium">
                  {g}
                </span>
              ))}
            </div>
          )}

          <h3
            className="text-xs md:text-sm font-bold text-white line-clamp-1 drop-shadow card-hover-title"
            dir={titleDir}
          >
            {displayTitle}
          </h3>
          {displaySubtitle && (
            <p className="text-[10px] text-neutral-300 line-clamp-1 drop-shadow-sm font-medium mt-0.5 card-hover-subtitle" dir={subtitleDir}>
              {displaySubtitle}
            </p>
          )}

          {item.episode && (
            <p className="text-[11px] text-neutral-300 line-clamp-1 mt-0.5">
              S{item.episode.seasonNumber}:E{item.episode.episodeNumber} {item.episode.title}
            </p>
          )}

          {/* Audio & Video Quality Badges */}
          <div className="flex items-center gap-1.5 mt-1 pointer-events-none">
            <span className="text-[8.5px] font-black px-1.5 py-0.5 rounded bg-white/20 text-white border border-white/20 leading-none">
              4K UHD
            </span>
            <span className="text-[8.5px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-neutral-200 leading-none">
              HDR10
            </span>
            <span className="text-[8.5px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-neutral-200 leading-none">
              5.1
            </span>
            {hasHebrew && (
              <span className="text-[8.5px] font-bold px-1.5 py-0.5 rounded bg-[#E50914]/30 text-white border border-[#E50914]/40 leading-none">
                עברית
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onPlay) {
                  onPlay(item.mediaItemId || item.id);
                } else {
                  onSelect(item.mediaItemId || item.id);
                }
              }}
              className="flex items-center justify-center h-8 w-8 rounded-full bg-white text-black hover:bg-neutral-200 transition-all shadow-md cursor-pointer btn-interactive active:scale-90"
              title={t('playLocal')}
            >
              <Play className="h-4 w-4 fill-current ml-0.5" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item.mediaItemId || item.id);
              }}
              className="flex items-center justify-center h-8 w-8 rounded-full bg-white/20 text-white hover:bg-white/30 transition-all backdrop-blur-sm cursor-pointer btn-interactive active:scale-90"
              title={t('moreInfo')}
            >
              <Info className="h-4 w-4" />
            </button>

            <button
              onClick={handleToggleWatchlist}
              className={`flex items-center justify-center h-8 w-8 rounded-full border transition-all cursor-pointer btn-interactive active:scale-90 ${
                inWatchlist
                  ? 'border-emerald-500/50 bg-emerald-500/30 text-emerald-400'
                  : 'border-white/30 bg-black/40 text-white hover:border-white hover:bg-black/60'
              }`}
              title={inWatchlist ? t('inWatchlist') : t('addToWatchlist')}
            >
              {inWatchlist ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            </button>

            {item.rating && (
              <span className="text-[11px] font-bold text-emerald-400 ml-auto">
                {(item.rating * 10).toFixed(0)}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
